import DOMPurify, {Config, HookEvent, HookName} from 'dompurify';
import {chain} from 'lodash';

/*
 * Types.
 */

interface DomPurifyOptions {
  allowDataAttributes?: boolean;
}

type DomPurifyHook = (currentNode: Element) => void;
type DomPurifyHooks = {[name in HookName]?: DomPurifyHook};

export interface DomPurifyConfiguration {
  options?: DomPurifyOptions;
  hooks?: DomPurifyHooks;
}

interface DomPurifyConfigurationWithPlugins extends DomPurifyConfiguration {
  plugins?: ReadonlyArray<DomPurifyConfiguration>;
}

interface ExtendedConfig extends Config {
  hooks: DomPurifyHooks;
  plugins: ReadonlyArray<DomPurifyConfiguration>;
  RETURN_DOM_FRAGMENT: false;
  RETURN_DOM: false;
}

function isExtendedConfig(config: Config): config is ExtendedConfig {
  return 'hooks' in config && 'plugins' in config;
}

/*
 * Register middleware hooks.
 */

function makeHookHandler(name: HookName) {
  return (currentNode: Element, data: HookEvent, config: Config) => {
    if (!isExtendedConfig(config)) {
      return;
    }

    // List the handlers for this in the config.
    const pluginHandlers = config.plugins.map((p) => p.hooks && p.hooks[name]);
    const handlers = chain(pluginHandlers).concat(config.hooks[name]).compact().value();

    // Call the handlers in order.
    handlers.forEach((handler) => handler(currentNode));
  };
}

DOMPurify.addHook('beforeSanitizeElements', makeHookHandler('beforeSanitizeElements'));
DOMPurify.addHook('uponSanitizeElement', makeHookHandler('uponSanitizeElement'));
DOMPurify.addHook('afterSanitizeElements', makeHookHandler('afterSanitizeElements'));
DOMPurify.addHook('beforeSanitizeAttributes', makeHookHandler('beforeSanitizeAttributes'));
DOMPurify.addHook('uponSanitizeAttribute', makeHookHandler('uponSanitizeAttribute'));
DOMPurify.addHook('afterSanitizeAttributes', makeHookHandler('afterSanitizeAttributes'));
DOMPurify.addHook('beforeSanitizeShadowDOM', makeHookHandler('beforeSanitizeShadowDOM'));
DOMPurify.addHook('uponSanitizeShadowNode', makeHookHandler('uponSanitizeShadowNode'));
DOMPurify.addHook('afterSanitizeShadowDOM', makeHookHandler('afterSanitizeShadowDOM'));

/*
 * Default configuration.
 */

const defaultConfiguration: Config = {
  FORBID_TAGS: ['iframe', 'meta', 'script', 'title'],
  ALLOW_DATA_ATTR: false,
  FORCE_BODY: true,
};

/*
 * Sanitization API.
 */

/** Sanitize HTML content according to the specified configuration. */
export function sanitizeHtml(html: string, configuration?: DomPurifyConfigurationWithPlugins) {
  // Merge the configuration into a consistent extended config.
  const extendedConfiguration: ExtendedConfig = {
    ...defaultConfiguration,
    ...(configuration && configuration.options && convertOptions(configuration.options)),
    hooks: (configuration && configuration.hooks) || {},
    plugins: (configuration && configuration.plugins) || [],
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM: false,
  };

  // Use DomPurify to process the HTML.
  return String(DOMPurify.sanitize(html, extendedConfiguration));
}

/** Convert our options to the DOMPurify format. */
function convertOptions(options: DomPurifyOptions): Config {
  return {
    ALLOW_DATA_ATTR: options.allowDataAttributes,
  };
}
